/**
 * Process Section - How We Build
 * ================================
 * Animated timeline with 5 steps
 */

import React, { useState, useEffect, useRef } from 'react';
import { config } from '../../data/config';
import './Process.css';

// Process Step Component
const ProcessStep = ({ step, index, isInView, totalSteps }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [lineProgress, setLineProgress] = useState(0);

  useEffect(() => {
    if (isInView) {
      const stepDelay = 400 + index * 300;
      const lineDelay = stepDelay + 200;

      const stepTimer = setTimeout(() => setIsVisible(true), stepDelay);
      const lineTimer = setTimeout(() => setLineProgress(100), lineDelay);

      return () => {
        clearTimeout(stepTimer);
        clearTimeout(lineTimer);
      };
    }
  }, [isInView, index]);

  const isLast = index === totalSteps - 1;

  return (
    <div className={`process-step ${isVisible ? 'in-view' : ''}`}>
      {/* Icon Column */}
      <div className="step-icon-col">
        <div className="step-icon-wrap">
          <div className="step-icon-ring" />
          <div className="step-icon">
            <i className={`fas ${step.icon}`} />
          </div>
        </div>
        {/* Connecting Line */}
        {!isLast && (
          <div className="step-line">
            <div 
              className="step-line-fill" 
              style={{ height: `${lineProgress}%` }}
            />
          </div>
        )}
      </div>

      {/* Content Column */}
      <div className="step-content">
        <div className="step-number">{step.step}</div>
        <h3>{step.title}</h3>
        <p>{step.description}</p>
      </div>
    </div>
  );
};

// Particles Background
const ProcessParticles = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    const createParticles = () => {
      const count = window.innerWidth < 768 ? 25 : 50;
      particles = [];
      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 2 + 0.5,
          speedX: (Math.random() - 0.5) * 0.2,
          speedY: (Math.random() - 0.5) * 0.2,
          opacity: Math.random() * 0.3 + 0.1
        });
      }
    };

    const animate = () => {
      ctx.fillStyle = 'rgba(5, 5, 5, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particles.forEach(p => {
        p.x += p.speedX;
        p.y += p.speedY;

        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212, 168, 83, ${p.opacity})`;
        ctx.fill();
      });

      animationId = requestAnimationFrame(animate);
    };

    resize();
    createParticles();
    animate();

    window.addEventListener('resize', () => {
      resize();
      createParticles();
    });

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="process-canvas" />;
};

const Process = () => {
  const sectionRef = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className="process-section" ref={sectionRef} id="process">
      {/* Background - No particles */}
      <div className="process-bg">
        <div className="process-grid-bg" />
        <div className="process-vignette" />
      </div>

      <div className="process-container">
        {/* Header */}
        <div className={`process-header ${isInView ? 'in-view' : ''}`}>
          <div className="section-badge">
            <i className="fas fa-cogs" />
            <span>Our Process</span>
          </div>
          <h2 className="section-title">
            How We <span className="text-highlight">Build</span>
          </h2>
        </div>

        {/* Timeline */}
        <div className="process-timeline">
          {config.process.map((step, index) => (
            <ProcessStep
              key={step.step}
              step={step}
              index={index}
              isInView={isInView}
              totalSteps={config.process.length}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Process;
